import htmx from 'htmx.org'
import posthog, { BeforeSendFn } from 'posthog-js'
import Cookies, { CookieChangeListener } from 'universal-cookie'
import LazyLoad from 'vanilla-lazyload'
import Glide from '@glidejs/glide'
import '@tailwindplus/elements'
import '@glidejs/glide/dist/css/glide.core.min.css'
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import L from 'leaflet'
import 'leaflet.markercluster'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

// Fix Leaflet default marker icon paths
/* eslint-disable  @typescript-eslint/no-explicit-any */
delete (L.Icon.Default.prototype as any)._getIconUrl

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const trackingCookieName = 'goodcrowd-tracking-accepted'
const cookies = new Cookies()

// Stamps the page context (rendered on <body> by the layout) onto every event, including the
// automatic $pageview. Campaigns are served from '/:slug', so the path alone cannot tell a
// campaign page apart from a static one — `page_type` is what makes that filterable in Posthog.
const addPageContext: BeforeSendFn = (event) => {
  if (!event) return event

  const { pageType, campaignId } = document.body?.dataset || {}

  event.properties = {
    ...(pageType ? { page_type: pageType } : {}),
    ...(campaignId ? { campaign_id: campaignId } : {}),
    ...event.properties,
  }

  return event
}

const initPosthog = () => {
  const { posthogApiKey, posthogApiHost } = document.body.dataset

  if (posthogApiKey && posthogApiHost && !posthog.__loaded) {
    posthog.init(posthogApiKey, {
      api_host: posthogApiHost,
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: false,
      disable_session_recording: true,
      person_profiles: 'always',
      before_send: addPageContext,
    })

    // Expose the initialised instance so other packs (e.g. campaign_show) can capture events
    // through shared/helpers/analytics without bundling and initialising posthog-js again.
    window.posthog = posthog
  }
}

const handleCookieChange: CookieChangeListener = (cookie) => {
  if (cookie.name === trackingCookieName && cookie.value == 'accepted') {
    initPosthog()
  }
}

cookies.addChangeListener(handleCookieChange)

if (cookies.get(trackingCookieName) === 'accepted') {
  initPosthog()
}

class AlignCenterElement extends HTMLElement {
  constructor() {
    super()
    this.attachShadow({ mode: 'open' })

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = `
      <style>
        :host {
          text-align: center;
          width: 100%;
          display: block;
        }
      </style>

      <slot></slot>
    `
    }
  }
}

htmx.onLoad(() => {
  if (!window.customElements.get('text-center')) {
    window.customElements.define('text-center', AlignCenterElement)
  }
})

// Lazy Loading
const lazyLoader = new LazyLoad()

htmx.onLoad(() => {
  lazyLoader.update()
})

htmx.onLoad(() => {
  const selectors = ['.glide', '#how-it-works', '#featured-articles', '#mobile-news-carousel']

  for (const selector of selectors) {
    if (document.querySelector(selector)) {
      new Glide(selector, {
        perView: 2,
        peek: 40,
        breakpoints: {
          450: { perView: 1 },
        },
      }).mount()
    }
  }

  // Initialize desktop news carousel separately (always shows 2 items)
  const desktopNewsCarousels = document.querySelectorAll('.glide-desktop')
  desktopNewsCarousels.forEach((carousel) => {
    new Glide(carousel as HTMLElement, {
      perView: 2,
      gap: 24, // gap-6 in Tailwind (24px)
      peek: 0,
    }).mount()
  })
})

// Open and close mobile menu
document.addEventListener('DOMContentLoaded', function (event) {
  const mobileMenuButton = document.getElementById('mobile-menu-button')
  const mobileMenu = document.getElementById('mobile-menu')

  if (!mobileMenu || !mobileMenuButton) {
    return
  }

  const outsideClick = (event: MouseEvent) => {
    const withinBoundaries = event.composedPath().includes(mobileMenu)

    if (!withinBoundaries && !mobileMenu.classList.contains('hidden')) {
      mobileMenu.classList.add('hidden')
      document.removeEventListener('click', outsideClick)
    }
  }

  mobileMenuButton.addEventListener('click', (event: MouseEvent) => {
    event.stopPropagation()

    if (mobileMenu.classList.contains('hidden')) {
      mobileMenu.classList.remove('hidden')
      document.addEventListener('click', outsideClick)
    } else {
      mobileMenu.classList.add('hidden')
      document.removeEventListener('click', outsideClick)
    }
  })
})

// Search form in header
document.addEventListener('DOMContentLoaded', function (event) {
  const searchForm = document.getElementById('search-form')
  const showSearchButton = document.getElementById('show-search-button')

  if (!searchForm || !showSearchButton) {
    return
  }

  showSearchButton.addEventListener('click', () => {
    const searchFormInput = searchForm.querySelector('input')

    searchForm.classList.toggle('hidden')
    showSearchButton.classList.toggle('hidden')

    if (searchFormInput) {
      searchFormInput.focus()
    }
  })
})

// Clear search input and results when the search dialog is closed
document.addEventListener('DOMContentLoaded', function () {
  const searchDialog = document.getElementById('search') as HTMLDialogElement | null
  if (!searchDialog) return

  searchDialog.addEventListener('close', () => {
    const input = searchDialog.querySelector<HTMLInputElement>('input[name="q"]')
    const results = document.getElementById('search-results')
    if (input) input.value = ''
    if (results) results.innerHTML = ''
  })
})

function heroTypeEffect() {
  let currentPhrase: string = ''
  let isDeleting: boolean = false
  let phraseIndex: number = 0
  let typingSpeed: number = 60

  const phraseElement = document.getElementById('typing-text')

  if (phraseElement?.getAttribute('effect-started') === '1') {
    return
  }

  phraseElement?.setAttribute('effect-started', '1')

  function typeEffect(): void {
    if (!phraseElement || !phraseElement.dataset.claims) return

    const phrases = JSON.parse(phraseElement.dataset.claims)
    const currentFullPhrase: string = phrases[phraseIndex]
    const speed: number = isDeleting ? 30 : 60

    if (!isDeleting && currentPhrase === currentFullPhrase) {
      setTimeout(() => {
        isDeleting = true
        typeEffect()
      }, 2000)
      return
    } else if (isDeleting && currentPhrase === '') {
      isDeleting = false
      phraseIndex = (phraseIndex + 1) % phrases.length
      setTimeout(typeEffect, 500) // Pause before typing the next phrase
      return
    }

    currentPhrase = isDeleting
      ? currentPhrase.substring(0, currentPhrase.length - 1)
      : currentFullPhrase.substring(0, currentPhrase.length + 1)

    phraseElement.innerHTML = `${currentPhrase}<span class="border-r-4 border-white animate-blink">&nbsp;</span>`
    typingSpeed = speed

    setTimeout(typeEffect, typingSpeed)
  }

  typeEffect()
}

document.addEventListener('htmx:afterSettle', heroTypeEffect)
document.addEventListener('DOMContentLoaded', heroTypeEffect)
window.addEventListener('htmx:historyRestore', heroTypeEffect)

htmx.onLoad(() => {
  const mapElement = document.getElementById('map')

  if (mapElement) {
    const markers = JSON.parse(mapElement.dataset.markers as string)
    const map = L.map('map', {}).setView([52.4989689, 13.4467688], 13)

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map)

    // Create marker cluster group
    const markerCluster = L.markerClusterGroup({
      chunkedLoading: true,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
    })

    const bounds: L.LatLngBoundsExpression = []

    for (const marker of markers) {
      const leafletMarker = L.marker([marker.lat, marker.lng]).bindPopup(
        `<strong>${marker.title}</strong><br><a href="${marker.link}" class="text-me-purple-700 hover:underline">View campaign</a>`
      )

      markerCluster.addLayer(leafletMarker)
      bounds.push([marker.lat, marker.lng])
    }

    // Add the cluster group to the map
    map.addLayer(markerCluster)

    // Fit map to show all markers
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] })
    }
  }
})
